import { noteError, noteEvent, patchDiagnostics } from '../lib/diagnostics';
import { env } from '../config/env';
import { AR_CANVAS_HEIGHT, AR_CANVAS_WIDTH } from '../ar/types';

/**
 * Camera and mic acquisition.
 *
 * On web this is the permission gate. On the kiosk the shell has pre-granted
 * both, so nothing is prompted — it is called to open the devices EARLY, while
 * the instruction clip plays, so the capture screen inherits a camera that is
 * already running. See KioskFlow.begin.
 *
 * One combined prompt in the happy path, because iOS Safari shows a separate
 * dialog per getUserMedia call and two dialogs before a guest has seen anything
 * is a drop-off we can avoid.
 *
 * Mic denial must not break the flow, so a combined failure falls back to
 * video-only and the session continues silent. Only a camera refusal is fatal.
 *
 * Camera Kit mode keeps the video tracks alive and hands them to the AR module
 * so we do not open the camera a second time. Stub / real still release video
 * here — those modules open their own stream at capture.
 */

export interface PermissionResult {
  cameraGranted: boolean;
  micGranted: boolean;
  /** Kept for the session. Null when the mic was refused or is absent. */
  micStream: MediaStream | null;
  /**
   * Live camera stream kept for Camera Kit. Null for stub/real (tracks stopped
   * after the grant) or when the camera was refused.
   */
  cameraStream: MediaStream | null;
  error: Error | null;
}

/**
 * True only when we are sure `requestPermissions` will raise no browser dialog,
 * so the priming screen can be skipped entirely.
 *
 * Deliberately conservative. The priming screen exists to explain the prompt
 * before it appears, so a false positive here is worse than showing the screen:
 * the guest gets a bare system dialog with no context. Hence both checks —
 * a granted camera is not enough if the mic would still ask.
 *
 * Returns false wherever the answer is unknown:
 *  - Safari rejects `permissions.query` for camera/microphone outright.
 *  - Firefox reports "prompt" even for a granted camera unless the guest ticked
 *    "Remember this decision", so it correctly falls through to the screen.
 * A granted permission also does not guarantee a working camera (another app
 * may hold it), so the caller must still handle a failed acquisition.
 */
export async function permissionsAlreadyGranted(): Promise<boolean> {
  if (!navigator.permissions?.query) return false;

  try {
    // `PermissionName` in TS's lib does not include the media device names,
    // though every browser that implements this accepts them.
    const camera = await navigator.permissions.query({
      name: 'camera' as PermissionName,
    });
    if (camera.state !== 'granted') return false;

    const mic = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    // "denied" is fine — the combined request falls back to a silent clip
    // without a dialog. Only "prompt" would surface one.
    return mic.state !== 'prompt';
  } catch {
    return false;
  }
}

export async function requestPermissions(options: {
  micDeviceId?: string;
  /**
   * Kiosk: the panel's camera, named explicitly. A signage unit can enumerate
   * several, and `facingMode: 'user'` is not a meaningful answer on one — so
   * when it is set we ask for that device and no other, the same `exact` the AR
   * module uses when it opens the camera itself.
   */
  cameraDeviceId?: string;
  /**
   * Kiosk: refuse rather than fall back to `facingMode`.
   *
   * On the kiosk panel the fallback is a hardware hazard, not a convenience.
   * No camera there reports as "user" (the USB webcam enumerates as EXTERNAL),
   * so the ideal constraint is dropped and Chromium opens the default device —
   * which may be the RK628 HDMI bridge that kernel-panics the board when opened
   * with no signal. See selectKioskCamera.ts and docs/KIOSK_HARDWARE.md.
   */
  requireDeviceId?: boolean;
  noiseSuppression?: boolean;
} = {}): Promise<PermissionResult> {
  if (!navigator.mediaDevices?.getUserMedia) {
    const error = new Error('getUserMedia unavailable — needs a secure context');
    noteError('permissions', error);
    return {
      cameraGranted: false,
      micGranted: false,
      micStream: null,
      cameraStream: null,
      error,
    };
  }

  if (options.requireDeviceId && !options.cameraDeviceId) {
    const error = new Error(
      'kiosk: no camera device id resolved — refusing to open a default camera',
    );
    noteError('permissions.camera', error);
    return {
      cameraGranted: false,
      micGranted: false,
      micStream: null,
      cameraStream: null,
      error,
    };
  }

  const keepCamera = env.arModule === 'camera-kit';

  const audioConstraints: MediaTrackConstraints = {
    noiseSuppression: options.noiseSuppression ?? false,
    echoCancellation: false,
    autoGainControl: false,
    ...(options.micDeviceId ? { deviceId: { exact: options.micDeviceId } } : {}),
  };

  /**
   * Portrait, because the clip is 9:16 and the compositor cover-crops to it.
   *
   * This asked for landscape 1280x720, which on a phone meant the crop threw
   * away two thirds of every frame: a 405x720 sliver upscaled into a 720x1280
   * clip. Asking the sensor for the shape we actually want costs nothing and
   * keeps the pixels.
   *
   * Every constraint is `ideal`, never `exact` — a laptop webcam that only does
   * landscape still opens, and simply gets cropped the way it always was.
   */
  const videoConstraints: MediaTrackConstraints = {
    ...(options.cameraDeviceId
      ? { deviceId: { exact: options.cameraDeviceId } }
      : { facingMode: 'user' }),
    width: { ideal: AR_CANVAS_WIDTH },
    height: { ideal: AR_CANVAS_HEIGHT },
    aspectRatio: { ideal: AR_CANVAS_WIDTH / AR_CANVAS_HEIGHT },
  };

  // Attempt 1: camera + mic together.
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: audioConstraints,
    });
    return splitStream(stream, true, keepCamera);
  } catch (err) {
    noteEvent(`combined permission request failed (${describe(err)}) — trying camera only`);
  }

  // Attempt 2: camera alone. A silent clip is a complete experience.
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: videoConstraints,
      audio: false,
    });
    const result = splitStream(stream, false, keepCamera);
    noteEvent('camera granted, no mic — clip will be silent');
    return result;
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    noteError('permissions.camera', error);
    patchDiagnostics({ micGranted: false });
    return {
      cameraGranted: false,
      micGranted: false,
      micStream: null,
      cameraStream: null,
      error,
    };
  }
}

/**
 * Splits audio and video. When keepCamera is false, video tracks are stopped
 * so the AR module can reopen them later. When true, they stay live.
 */
function splitStream(
  stream: MediaStream,
  expectAudio: boolean,
  keepCamera: boolean,
): PermissionResult {
  const audioTracks = stream.getAudioTracks();
  const videoTracks = stream.getVideoTracks();

  let cameraStream: MediaStream | null = null;
  if (keepCamera && videoTracks.length > 0) {
    cameraStream = new MediaStream(videoTracks);
  } else {
    videoTracks.forEach((t) => {
      stream.removeTrack(t);
      t.stop();
    });
  }

  const micStream = audioTracks.length > 0 ? new MediaStream(audioTracks) : null;
  const micGranted = micStream !== null;

  patchDiagnostics({
    micGranted,
    micDeviceId: audioTracks[0]?.getSettings().deviceId ?? null,
    micLabel: audioTracks[0]?.label ?? null,
  });

  if (expectAudio && !micGranted) {
    noteEvent('permission granted but no audio track present');
  }

  return {
    cameraGranted: true,
    micGranted,
    micStream,
    cameraStream,
    error: null,
  };
}

function describe(err: unknown): string {
  if (err instanceof DOMException) return err.name;
  return err instanceof Error ? err.message : String(err);
}
